import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { useResizeObserver, filterDOMProps } from '@react-aria/utils';
import React, { forwardRef, useMemo, useRef, useState, useCallback } from 'react';
import { useFilter } from 'react-aria';
import { ListBoxContext, FormContext, ComboBoxStateContext, LabelContext, ButtonContext, InputContext, OverlayTriggerStateContext, PopoverContext, ListStateContext, TextContext, GroupContext, FieldErrorContext, ComboBoxContext } from 'react-aria-components';
import { useComboBoxState } from 'react-stately';
import { useComboBox } from './use-combo-box.esm.js';
import { useCollectionDocument, CollectionDocumentContext } from '../../collections/aria/collection.esm.js';
import { useContextProps, Hidden, Provider, useSlottedContext, useSlot, removeDataAttributes, useRenderProps } from '../../utils/index.esm.js';

var _excluded = ["buttonProps", "inputProps", "listBoxProps", "labelProps", "descriptionProps", "errorMessageProps"];
function ComboBoxInner(_ref) {
  var _ref3, _props$validationBeha;
  var props = _ref.props,
    collection = _ref.collection,
    ref = _ref.comboBoxRef;
  var name = props.name,
    allowsCustomValue = props.allowsCustomValue;
  var _props$formValue = props.formValue,
    formValue = _props$formValue === void 0 ? 'key' : _props$formValue;
  if (allowsCustomValue) {
    formValue = 'text';
  }
  var _ref2 = useSlottedContext(FormContext) || {},
    formValidationBehavior = _ref2.validationBehavior;
  var validationBehavior = (_ref3 = (_props$validationBeha = props.validationBehavior) !== null && _props$validationBeha !== void 0 ? _props$validationBeha : formValidationBehavior) !== null && _ref3 !== void 0 ? _ref3 : 'native';
  var _useFilter = useFilter({
      sensitivity: 'base'
    }),
    contains = _useFilter.contains;
  var state = useComboBoxState(_objectSpread2(_objectSpread2({
    defaultFilter: props.defaultFilter || contains
  }, props), {}, {
    // If props.items isn't provided, rely on collection filtering (aka listbox.items is provided or defaultItems provided to ComboBox)
    items: props.items,
    children: undefined,
    collection: collection,
    validationBehavior: validationBehavior
  }));
  var buttonRef = useRef(null);
  var inputRef = useRef(null);
  var listBoxRef = useRef(null);
  var popoverRef = useRef(null);
  var _useSlot = useSlot(),
    _useSlot2 = _slicedToArray(_useSlot, 2),
    labelRef = _useSlot2[0],
    label = _useSlot2[1];
  var _useComboBox = useComboBox(_objectSpread2(_objectSpread2({}, removeDataAttributes(props)), {}, {
      label: label,
      inputRef: inputRef,
      buttonRef: buttonRef,
      listBoxRef: listBoxRef,
      popoverRef: popoverRef,
      name: formValue === 'text' ? name : undefined,
      validationBehavior: validationBehavior
    }), state),
    buttonProps = _useComboBox.buttonProps,
    inputProps = _useComboBox.inputProps,
    listBoxProps = _useComboBox.listBoxProps,
    labelProps = _useComboBox.labelProps,
    descriptionProps = _useComboBox.descriptionProps,
    errorMessageProps = _useComboBox.errorMessageProps,
    validation = _objectWithoutProperties(_useComboBox, _excluded);

  // Make menu width match input + button
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    menuWidth = _useState2[0],
    setMenuWidth = _useState2[1];
  var onResize = useCallback(function () {
    if (inputRef.current) {
      var _buttonRef$current;
      var buttonRect = (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.getBoundingClientRect();
      var inputRect = inputRef.current.getBoundingClientRect();
      var minX = buttonRect ? Math.min(buttonRect.left, inputRect.left) : inputRect.left;
      var maxX = buttonRect ? Math.max(buttonRect.right, inputRect.right) : inputRect.right;
      setMenuWidth(maxX - minX + 'px');
    }
  }, [buttonRef, inputRef, setMenuWidth]);
  useResizeObserver({
    ref: inputRef,
    onResize: onResize
  });

  // Only expose a subset of state to renderProps function to avoid infinite render loop
  var renderPropsState = useMemo(function () {
    return {
      isOpen: state.isOpen,
      isDisabled: props.isDisabled || false,
      isInvalid: validation.isInvalid || false,
      isRequired: props.isRequired || false
    };
  }, [state.isOpen, props.isDisabled, validation.isInvalid, props.isRequired]);
  var renderProps = useRenderProps(_objectSpread2(_objectSpread2({}, props), {}, {
    values: renderPropsState,
    defaultClassName: 'react-aria-ComboBox'
  }));
  var DOMProps = filterDOMProps(props);
  delete DOMProps.id;
  return /*#__PURE__*/React.createElement(Provider
  // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
  , {
    values: [[ComboBoxStateContext, state], [LabelContext, _objectSpread2(_objectSpread2({}, labelProps), {}, {
      ref: labelRef
    })], [ButtonContext, _objectSpread2(_objectSpread2({}, buttonProps), {}, {
      ref: buttonRef,
      isPressed: state.isOpen
    })], [InputContext, _objectSpread2(_objectSpread2({}, inputProps), {}, {
      ref: inputRef
    })], [OverlayTriggerStateContext, state], [PopoverContext, {
      ref: popoverRef,
      triggerRef: inputRef,
      placement: 'bottom start',
      isNonModal: true,
      trigger: 'ComboBox',
      style: {
        '--trigger-width': menuWidth
      }
    }], [ListBoxContext, _objectSpread2(_objectSpread2({}, listBoxProps), {}, {
      ref: listBoxRef
    })], [ListStateContext, state], [TextContext, {
      slots: {
        description: descriptionProps,
        errorMessage: errorMessageProps
      }
    }], [GroupContext, {
      isInvalid: validation.isInvalid,
      isDisabled: props.isDisabled || false
    }], [FieldErrorContext, validation]]
  }, /*#__PURE__*/React.createElement("div", _extends({}, DOMProps, renderProps, {
    ref: ref,
    slot: props.slot || undefined,
    "data-focused": state.isFocused || undefined,
    "data-open": state.isOpen || undefined,
    "data-disabled": props.isDisabled || undefined,
    "data-invalid": validation.isInvalid || undefined,
    "data-required": props.isRequired || undefined
  })), name && formValue === 'key' && /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: name,
    value: state.selectedKey
  }));
}
function ComboBox(props, ref) {
  var _props$items;
  var _useContextProps = useContextProps(props, ref, ComboBoxContext);
  var _useContextProps2 = _slicedToArray(_useContextProps, 2);
  props = _useContextProps2[0];
  ref = _useContextProps2[1];
  var _useCollectionDocumen = useCollectionDocument(),
    collection = _useCollectionDocumen.collection,
    document = _useCollectionDocumen.document;
  var _props = props,
    _children = _props.children,
    _props$isDisabled = _props.isDisabled,
    isDisabled = _props$isDisabled === void 0 ? false : _props$isDisabled,
    _props$isInvalid = _props.isInvalid,
    isInvalid = _props$isInvalid === void 0 ? false : _props$isInvalid,
    _props$isRequired = _props.isRequired,
    isRequired = _props$isRequired === void 0 ? false : _props$isRequired;
  var children = useMemo(function () {
    return typeof _children === 'function' ? _children({
      isOpen: false,
      isDisabled: isDisabled,
      isInvalid: isInvalid,
      isRequired: isRequired,
      defaultChildren: null
    }) : _children;
  }, [_children, isDisabled, isInvalid, isRequired]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Hidden, null, /*#__PURE__*/React.createElement(Provider
  // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
  , {
    values: [[CollectionDocumentContext, document], [ListBoxContext, {
      items: (_props$items = props.items) !== null && _props$items !== void 0 ? _props$items : props.defaultItems
    }]]
  }, children)), /*#__PURE__*/React.createElement(ComboBoxInner, {
    props: props,
    collection: collection,
    comboBoxRef: ref
  }));
}

/**
 * A combo box combines a text input with a listbox, allowing users to filter a list of options to items matching a query.
 */
var _ComboBox = /*#__PURE__*/forwardRef(ComboBox);

export { _ComboBox as default };
//# sourceMappingURL=combo-box.esm.js.map
